import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogoutUser } from "../../../services/usersService";
import {
  Box,
  CssBaseline,
  Drawer,
  Divider,
  Typography,
} from "@mui/material";
import DrawerHeader from "./DrawerHeader";
import DrawerList from "./DrawerList";
import SignOutButton from "./SignOutButton";
import UserService from "../../../globel-constants/UserService";
import pages from "./pages"; // Import pages structure here
import { isMobile } from "react-device-detect";

const minDrawerWidth = 60;
const maxDrawerWidth = 400;

function NewVLPHeader(props) {
  const [drawerWidth, setDrawerWidth] = useState(240);
  const [mobileOpen, setMobileOpen] = useState(!isMobile);
  const [loggedInUser, setLoggedInUser] = useState();
  const [userTitle, setUserTitle] = useState("");
  const [openStates, setOpenStates] = useState({
    Orders: false,
    Billings: false,
    Reports: false,
    Maintenance: false,
    Settings: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const loggedInUser = localStorage.getItem("loggedInUser");
    const userObject = JSON.parse(loggedInUser);
    setUserTitle(userObject.aliasName);
    setLoggedInUser(UserService.getUserInfoFromLocalStorage());
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggle = (label) => {
    setOpenStates((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const onRouteClick = (route) => {    
    if (route === "scanner") {
      navigate(`/vlp-extend`);
    } else {
      if (route === "settings" && !loggedInUser.limsAdministrator) {
        navigate(`/vlp/unauthorized`);
      } else {
        navigate(`/vlp/${route}`);
      }
    }

    if (isMobile) {
      setMobileOpen(!isMobile);
    }
  };

  const handleSignOut = async () => {
    await LogoutUser();
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("lastUser");
    localStorage.removeItem("sessionExpiryWarningsShown");
    window.location.pathname = "/";
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const newWidth =
      e.clientX > maxDrawerWidth
        ? maxDrawerWidth
        : e.clientX < minDrawerWidth
        ? minDrawerWidth
        : e.clientX;
    setDrawerWidth(newWidth);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        sx={{
          width: mobileOpen ? drawerWidth : minDrawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: mobileOpen ? drawerWidth : minDrawerWidth,
            transition: "width 0.3s",
            boxSizing: "border-box",
            bgcolor: "#1976d2",
            color: "white",
            display: "flex",
            flexDirection: "column",
          },
        }}
        open={mobileOpen}
      >
        {/* Fixed Header Section */}
        <Box sx={{ flexShrink: 0 }}>
          <DrawerHeader
            mobileOpen={mobileOpen}
            handleDrawerToggle={handleDrawerToggle}
          />
          <Divider />
        </Box>

        {/* Scrollable Menu Section */}
        <Box 
          sx={{ 
            flex: 1, 
            position: "relative",
            overflowY: "auto",
            overflowX: "hidden",
            // Hide scrollbar for WebKit browsers (Chrome, Safari, Edge)
            "&::-webkit-scrollbar": {
              display: "none",
            },
            // Hide scrollbar for Firefox
            scrollbarWidth: "none",
            // For IE and Edge legacy
            msOverflowStyle: "none",
          }}
          onScroll={(e) => {
            const target = e.target;
            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            const clientHeight = target.clientHeight;
            
            // Update scroll indicators
            const topIndicator = target.parentElement.querySelector('.scroll-indicator-top');
            const bottomIndicator = target.parentElement.querySelector('.scroll-indicator-bottom');
            
            if (topIndicator) {
              topIndicator.style.opacity = scrollTop > 10 ? '1' : '0';
            }
            if (bottomIndicator) {
              bottomIndicator.style.opacity = scrollTop < scrollHeight - clientHeight - 10 ? '1' : '0';
            }
          }}
        >
          <DrawerList
            pages={pages}
            mobileOpen={mobileOpen}
            onRouteClick={onRouteClick}
            openStates={openStates}
            handleToggle={handleToggle}
          />
        </Box>
        
        {/* Scroll Indicators */}
        {mobileOpen && (
          <>
            {/* Top Scroll Indicator */}
            <Box
              className="scroll-indicator-top"
              sx={{
                position: "absolute",
                top: "64px", // Adjust based on your header height
                left: 0,
                right: 0,
                height: "20px",
                background: "linear-gradient(to bottom, rgba(25, 118, 210, 0.8), transparent)",
                pointerEvents: "none",
                opacity: 0,
                transition: "opacity 0.3s ease",
                zIndex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                paddingTop: "2px"
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "white", 
                  fontSize: "10px",
                  opacity: 0.8,
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                  fontSize: "9px"
                }}
              >
                ▲ More above
              </Typography>
            </Box>
            
            {/* Bottom Scroll Indicator */}
            <Box
              className="scroll-indicator-bottom"
              sx={{
                position: "absolute",
                bottom: mobileOpen ? "120px" : "20px", // Adjust based on your footer height
                left: 0,
                right: 0,
                height: "20px",
                background: "linear-gradient(to top, rgba(25, 118, 210, 0.8), transparent)",
                pointerEvents: "none",
                opacity: 1, // Initially visible to indicate scrollable content
                transition: "opacity 0.3s ease",
                zIndex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
                paddingBottom: "2px"
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: "white", 
                  fontSize: "10px",
                  opacity: 0.8,
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)"
                }}
              >
                ▼ More below
              </Typography>
            </Box>
          </>
        )}

        {/* Fixed Footer Section */}
        {mobileOpen && (
          <Box sx={{ flexShrink: 0 }}>
            <Divider />
            <Box sx={{ padding: "5px", textAlign: "center" }}>
              <Typography>{userTitle}</Typography>
            </Box>
            <Divider />
            <SignOutButton handleSignOut={handleSignOut} mobileOpen={mobileOpen} />
          </Box>
        )}
      </Drawer>
      {/* Drawer Resizer Handle */}
      <Box
        sx={{
          width: "5px",
          cursor: "col-resize",
          backgroundColor: "#1976d2",
          position: "relative",
          zIndex: 1201,
          height: "100vh",
        }}
        onMouseDown={handleMouseDown}
      />
    </Box>
  );
}

export default NewVLPHeader;